import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../hooks/useChat.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { hasRole } from '../../../lib/permissions.js';
import { ChatMessage } from './ChatMessage.js';
import { ChatInput } from './ChatInput.js';
import type { ChatConfig, PortalRole } from './types.js';

interface ChatPanelProps {
  /** チャット設定 */
  config: ChatConfig;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャットパネル。折りたたみ/全画面展開対応のSSEストリーミングチャットUI。権限ベースのアクセス制御・セッションリセット・多言語対応を統合
 * @graph-connects useChat [calls] SSEストリーミング通信でAIチャットメッセージを送受信
 * @graph-connects useAuth [calls] ユーザー情報と権限を取得しアクセス制御に使用
 * @graph-connects hasRole [calls] ロール判定ヘルパー呼び出し
 * @graph-connects ChatMessage [calls] チャットメッセージ表示コンポーネント呼び出し
 * @graph-connects ChatInput [calls] チャット入力フォームコンポーネント呼び出し
 */
export function ChatPanel({ config }: ChatPanelProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 現在の言語設定を取得（ja, en, vi のいずれか）
  const langCode = i18n.language.split('-')[0];
  const locale = (['ja', 'en', 'vi'].includes(langCode) ? langCode : 'ja') as 'ja' | 'en' | 'vi';

  const { messages, isStreaming, error, sendMessage, reset, clearError } = useChat({
    endpoint: config.endpoint,
    locale,
    dashboardContext: config.dashboardContext,
  });

  // 権限チェック
  const userPortalRoles = user?.portalRoles;
  const hasAccess = hasRole(
    userPortalRoles,
    config.allowedRoles as unknown as readonly PortalRole[],
  );

  // メッセージが追加されたら自動スクロール
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // パネルを開いたときに一番下にスクロール
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSend = useCallback(
    (message: string) => {
      void sendMessage(message);
    },
    [sendMessage],
  );

  const handleReset = useCallback(() => {
    if (window.confirm(t('chat.resetConfirm', '会話をリセットしますか？'))) {
      reset();
    }
  }, [reset, t]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // アクセス権がない場合は表示しない
  if (!hasAccess) {
    return null;
  }

  // Portalを使用してdocument.bodyに直接レンダリング
  // これにより、親要素のtransformによるposition:fixedの破壊を回避
  return createPortal(
    <div
      className={`fixed ${isExpanded ? 'inset-0 p-4 z-[var(--z-modal)]' : 'bottom-4 right-4 z-50'}`}
    >
      {/* 折りたたまれた状態: トグルボタン */}
      {!isOpen && (
        <button
          type="button"
          onClick={handleToggle}
          className="
            flex items-center gap-2
            px-4 py-3
            bg-[var(--color-accent-primary)]
            hover:bg-[var(--color-accent-hover)]
            active:bg-[var(--color-accent-active)]
            text-white font-medium
            rounded-full
            shadow-lg
            transition-all duration-[var(--transition-fast)]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]
          "
          aria-label={t('chat.open', 'チャットを開く')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z"
              clipRule="evenodd"
            />
          </svg>
          <span>{config.title}</span>
        </button>
      )}

      {/* 展開された状態: チャットパネル */}
      {isOpen && (
        <div
          className={`
            flex flex-col
            bg-[var(--color-bg-surface)]
            border border-[var(--color-border)]
            rounded-xl
            shadow-2xl
            overflow-hidden
            transition-all duration-200
            ${isExpanded ? 'w-full h-full' : 'w-[400px] h-[600px] max-h-[80vh]'}
          `}
        >
          {/* ヘッダー */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5 text-[var(--color-accent-primary)]"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"
                  clipRule="evenodd"
                />
              </svg>
              <h2 className="font-semibold text-[var(--color-text-primary)]">{config.title}</h2>
            </div>
            <div className="flex items-center gap-1">
              {/* リセットボタン */}
              <button
                type="button"
                onClick={handleReset}
                className="
                  p-2
                  text-[var(--color-text-muted)]
                  hover:text-[var(--color-text-primary)]
                  hover:bg-[var(--color-bg-hover)]
                  rounded-lg
                  transition-all duration-[var(--transition-fast)]
                "
                aria-label={t('chat.reset', '会話をリセット')}
                title={t('chat.reset', '会話をリセット')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0V5.36l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {/* 拡大/縮小ボタン */}
              <button
                type="button"
                onClick={handleToggleExpand}
                className="
                  p-2
                  text-[var(--color-text-muted)]
                  hover:text-[var(--color-text-primary)]
                  hover:bg-[var(--color-bg-hover)]
                  rounded-lg
                  transition-all duration-[var(--transition-fast)]
                "
                aria-label={isExpanded ? t('chat.shrink', '縮小') : t('chat.expand', '拡大')}
                title={isExpanded ? t('chat.shrink', '縮小') : t('chat.expand', '拡大')}
              >
                {isExpanded ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3.28 2.22a.75.75 0 00-1.06 1.06L5.44 6.5H2.75a.75.75 0 000 1.5h5a.75.75 0 00.75-.75v-5a.75.75 0 00-1.5 0v2.69L3.28 2.22zm13.44 0l-3.72 3.72V3.25a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h5a.75.75 0 000-1.5h-2.69l3.72-3.72a.75.75 0 00-1.06-1.06zM3.28 17.78l3.72-3.72v2.69a.75.75 0 001.5 0v-5a.75.75 0 00-.75-.75h-5a.75.75 0 000 1.5h2.69l-3.72 3.72a.75.75 0 101.06 1.06zm13.44 0a.75.75 0 01-1.06 0l-3.72-3.72v2.69a.75.75 0 01-1.5 0v-5c0-.414.336-.75.75-.75h5a.75.75 0 010 1.5h-2.69l3.72 3.72a.75.75 0 010 1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M15 3.75a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V5.56l-3.97 3.97a.75.75 0 1 1-1.06-1.06l3.97-3.97h-2.69a.75.75 0 0 1-.75-.75Zm-12 0A.75.75 0 0 1 3.75 3h4.5a.75.75 0 0 1 0 1.5H5.56l3.97 3.97a.75.75 0 0 1-1.06 1.06L4.5 5.56v2.69a.75.75 0 0 1-1.5 0v-4.5Zm11.47 11.78a.75.75 0 1 1 1.06-1.06l3.97 3.97v-2.69a.75.75 0 0 1 1.5 0v4.5a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1 0-1.5h2.69l-3.97-3.97Zm-4.94-1.06a.75.75 0 0 1 0 1.06L5.56 19.5h2.69a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 1 1.5 0v2.69l3.97-3.97a.75.75 0 0 1 1.06 0Z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
              {/* 閉じるボタン */}
              <button
                type="button"
                onClick={handleToggle}
                className="
                  p-2
                  text-[var(--color-text-muted)]
                  hover:text-[var(--color-text-primary)]
                  hover:bg-[var(--color-bg-hover)]
                  rounded-lg
                  transition-all duration-[var(--transition-fast)]
                "
                aria-label={t('chat.close', 'チャットを閉じる')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          </div>

          {/* メッセージ一覧 */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* ウェルカムメッセージ */}
            {messages.length === 0 && config.welcomeMessage && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-6 h-6 text-[var(--color-accent-primary)]"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {config.welcomeMessage}
                </p>
              </div>
            )}

            {/* メッセージ */}
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id}
                message={message}
                isStreaming={
                  isStreaming && index === messages.length - 1 && message.role === 'assistant'
                }
              />
            ))}

            {/* エラー表示 */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-5 h-5 text-[var(--color-error)]"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="flex-1 text-sm text-[var(--color-error)]">{error}</span>
                <button
                  type="button"
                  onClick={clearError}
                  className="text-[var(--color-error)] hover:text-[var(--color-error)]/80"
                  aria-label={t('chat.dismissError', 'エラーを閉じる')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* 入力エリア */}
          <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
            <ChatInput
              onSend={handleSend}
              disabled={isStreaming}
              placeholder={config.placeholder}
            />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
